import { useEffect, useSyncExternalStore, MouseEvent } from 'react';
import {
  GameData,
  MESSAGE_STATUS,
  useApiContext,
  useGetFullInfo,
  useMessageContext
} from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useGameAvailabilityCheck } from '../hooks/useGameCheck';
import { useModalContext } from 'src/hooks/useModalContext';
import { useRoute } from 'src/router/hooks';
import { useGameFavorites } from '../hooks/useGameFavorites';
import { linkFavoritesStore } from 'src/store';
import { useAuthFlow } from 'src/modules/authFlow/hooks/useAuthFlow';

interface UseGameHandleClickProps {
  game: GameData;
  onClick?: () => void;
}

const PLAY_N_GO_ID = 27;
const QUICKFIRE_ID = 101;

export const useGameHandleClick = ({ game, onClick }: UseGameHandleClickProps) => {
  const { isAuthenticated } = useApiContext();
  const { refetch } = useGetFullInfo({ enabled: false });
  const { openMessage } = useMessageContext();
  const { openModal } = useModalContext();
  const { ROUTE } = useRoute();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();

  const { startRegistration } = useAuthFlow();
  const gameId = game?.id;
  const gameCheck = useGameAvailabilityCheck();
  const providerId = PLAY_N_GO_ID || QUICKFIRE_ID;
  const { toggleFavorite: toggleFavoriteMutation, isFavoriteLoading } = useGameFavorites(game?.id);

  const { getSnapshot, subscribe, updateSnapshot } = linkFavoritesStore(
    game?.id,
    Boolean(game?.favorite)
  );
  const isFavorite = useSyncExternalStore(subscribe, getSnapshot);

  const toggleFavorite = (): void => {
    const handleFavorite = () => {
      const optimisticUpdate = !isFavorite;
      updateSnapshot(optimisticUpdate);
      toggleFavoriteMutation(Boolean(isFavorite));
    };
    if (!isAuthenticated) {
      startRegistration({ onSuccess: handleFavorite });
    } else {
      handleFavorite();
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      updateSnapshot(false);
    }
  }, [isAuthenticated]);

  const navigateToGame = ({ isDemo }: { isDemo: boolean }) => {
    const basePath = lang ? location.pathname.replace(`/${lang}`, '') : location.pathname;
    const gameName = game.name.replace(/[[\].,\s!*;&+#:@'()^"$=+\\?/]/g, '');

    navigate(`${ROUTE.GAME.get(`${game.id}`, isDemo ? 'demo' : '')}?name=${gameName}`, {
      state: { history: basePath, search: location?.search }
    });
  };

  const handlePlayGameClick = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
    e.preventDefault();

    const handlePlay = async () => {
      if (!gameCheck(gameId)) {
        openMessage?.(t('gameNotification.notAvailable'), MESSAGE_STATUS.ERROR);
        return;
      }

      const { data } = await refetch();

      if (game.providerId === providerId) {
        data?.data.birthday
          ? navigateToGame({ isDemo: false })
          : openModal('PROFILE_REQUIREMENTS', {
              props: { navigateCallback: () => navigateToGame({ isDemo: false }) }
            });
        return;
      }

      onClick?.();
      navigateToGame({ isDemo: false });
    };

    if (!isAuthenticated) {
      startRegistration({ props: { showImageBg: true }, onSuccess: handlePlay });
    } else {
      handlePlay();
    }
  };

  const handlePlayDemoGameClick = () => {
    onClick?.();
    navigateToGame({ isDemo: true });
  };

  return {
    handlePlayGameClick,
    handlePlayDemoGameClick,
    toggleFavorite,
    isFavorite: Boolean(isFavorite),
    isFavoriteLoading
  };
};
